import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Modal, Progress, Select, Space, Table, Tag, Typography, Upload, message } from "antd";
import { DownloadOutlined, InboxOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { importLeadsApi, validateImportApi } from "../../../../apiservice/crm";
import { fetchLeadPurpose, fetchLeadSource, fetchLeadStatus } from "../leadThunk";
import { selectAuthInfo } from "../../../Auth/authSelectors";

// crypto.randomUUID() is available in every modern browser (HTTPS/localhost)
// -- avoids pulling in the `uuid` package just for this.
const newRunId = () =>
  (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `run-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const { Text } = Typography;

const MAX_FILE_BYTES = 2 * 1024 * 1024;
const MAX_ROWS = 1000;
const CHUNK_SIZE = 100; // the server accepts at most 100 rows per request
// The API has a small (100 KB) JSON body limit, so chunks are also capped by payload size.
const CHUNK_MAX_BYTES = 80 * 1024;

const buildChunks = (allRows) => {
  const chunks = [];
  let current = [];
  let bytes = 0;
  allRows.forEach((r) => {
    const size = JSON.stringify(r).length;
    if (current.length && (current.length >= CHUNK_SIZE || bytes + size > CHUNK_MAX_BYTES)) {
      chunks.push(current);
      current = [];
      bytes = 0;
    }
    current.push(r);
    bytes += size;
  });
  if (current.length) chunks.push(current);
  return chunks;
};

const norm = (v) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

// canonical field -> accepted header spellings (normalized)
const HEADER_ALIASES = {
  name: ["name", "leadname", "customername", "fullname"],
  phone: ["phone", "phonenumber", "mobile", "mobilenumber", "contactno", "contactnumber", "contact"],
  email: ["email", "emailaddress", "emailid"],
  company: ["company", "companyname", "institution", "organization", "organisation"],
  country: ["country"],
  address: ["address"],
  profession: ["profession", "jobtitle", "designation"],
  status: ["status", "leadstatus"],
  source: ["source", "leadsource"],
  purpose: ["purpose", "leadpurpose"],
  notes: ["notes", "remarks", "comment", "comments"],
};

const TEMPLATE_HEADERS = [
  "Name*", "Phone*", "Email", "Company", "Country", "Address", "Profession",
  "Status", "Source", "Purpose", "Notes",
];

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const LeadImportModal = ({ open, onClose, onDone, isAdmin, customFields = [] }) => {
  const dispatch = useDispatch();
  const { statusList, sourceList, purposeList } = useSelector((s) => s.leads);
  const users = useSelector((s) => s.lookups.users);
  const authInfo = useSelector(selectAuthInfo);
  const currentUserId = authInfo?.userInfo?.user_id;
  const currentUserName = authInfo?.userInfo?.full_name;

  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [assigneeId, setAssigneeId] = useState(null);
  const [autoCreateMissing, setAutoCreateMissing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [missingReport, setMissingReport] = useState(null);

  const hasStatuses = Boolean(statusList?.length);
  const hasSources = Boolean(sourceList?.length);
  const hasPurposes = Boolean(purposeList?.length);

  // Load the master lists the first time the modal opens (only while empty)
  // -- still used for the downloadable template's "Valid Status/Source/
  // Purpose" hints now that there's no in-modal dropdown to populate.
  useEffect(() => {
    if (!open) return;
    if (!hasStatuses) dispatch(fetchLeadStatus());
    if (!hasSources) dispatch(fetchLeadSource());
    if (!hasPurposes) dispatch(fetchLeadPurpose());
  }, [open, hasStatuses, hasSources, hasPurposes, dispatch]);

  // Each time it opens, the assignee starts as the logged-in user.
  useEffect(() => {
    if (open) setAssigneeId(currentUserId);
  }, [open, currentUserId]);

  // Checks the WHOLE file's status/source/purpose/custom-field values
  // against this branch's masters before any lead is created. Returns
  // whether it's safe to proceed with the real (chunked) import.
  const runValidate = async (rowsToCheck, autoCreate) => {
    setValidating(true);
    try {
      const res = await validateImportApi(rowsToCheck, { auto_create_missing: autoCreate });
      if (res.data?.ready === false) {
        setMissingReport(res.data.missing);
        return false;
      }
      setMissingReport(null);
      const created = res.data?.created;
      if (autoCreate && created) {
        const parts = [];
        if (created.status?.length) parts.push(`${created.status.length} status${created.status.length !== 1 ? "es" : ""}`);
        if (created.source?.length) parts.push(`${created.source.length} source${created.source.length !== 1 ? "s" : ""}`);
        if (created.purpose?.length) parts.push(`${created.purpose.length} purpose${created.purpose.length !== 1 ? "s" : ""}`);
        if (created.custom_fields?.length) parts.push(`${created.custom_fields.length} custom field option${created.custom_fields.length !== 1 ? "s" : ""}`);
        if (parts.length) message.success(`Created: ${parts.join(", ")}`);
      }
      return true;
    } catch (err) {
      message.error(err.message || "Could not validate the file");
      return false;
    } finally {
      setValidating(false);
    }
  };

  const reset = () => {
    setFileName("");
    setRows([]);
    setProgress(0);
    setResults(null);
    setImporting(false);
    setMissingReport(null);
  };

  const close = () => {
    if (importing) return;
    const imported = results?.some((r) => r.status === "imported");
    reset();
    onClose();
    if (imported) onDone?.();
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const headers = [...TEMPLATE_HEADERS, ...customFields.map((f) => f.field_name)];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([headers]), "Leads");

    const list = (items) => (items?.length ? items.map((i) => i.name).join(", ") : "-");
    const info = [
      ["How to fill this template"],
      ["Name* and Phone* are required. Everything else is optional."],
      ["Phone: include the country code (e.g. 971501234567) or fill Country so it can be added for you."],
      ["Leads whose phone number already exists are skipped, not overwritten."],
      ["Status: leave blank to use this branch's default status; any value is accepted here and can be created automatically."],
      ["Source blank = Import. Any value is accepted and can be created automatically too."],
      [isAdmin
        ? "The employee the leads are assigned to is chosen in the import window."
        : "Imported leads are assigned to you."],
      [],
      ["Valid Status", list(statusList)],
      ["Valid Source", list(sourceList)],
      ["Valid Purpose", list(purposeList)],
      ...customFields.map((f) => [
        `Custom field: ${f.field_name} (${f.type}${Number(f.is_required) === 1 ? ", required" : ""})`,
        (f.options || []).length ? f.options.join(f.type === "multi_select" ? "; " : ", ") : "",
      ]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(info), "Instructions");
    XLSX.writeFile(wb, "lead_import_template.xlsx");
  };

  const mapRows = (rawRows) => {
    const customByKey = {};
    customFields.forEach((f) => { customByKey[norm(f.field_name)] = f.field_name; });

    const aliasToCanonical = {};
    Object.entries(HEADER_ALIASES).forEach(([canonical, aliases]) =>
      aliases.forEach((a) => { aliasToCanonical[a] = canonical; })
    );

    const mapped = [];
    rawRows.forEach((raw, i) => {
      const out = { row: i + 2, custom: {} };
      let hasValue = false;
      Object.entries(raw).forEach(([header, value]) => {
        const v = value === null || value === undefined ? "" : String(value).trim();
        if (v) hasValue = true;
        const key = norm(header);
        if (aliasToCanonical[key]) out[aliasToCanonical[key]] = v;
        else if (customByKey[key]) out.custom[customByKey[key]] = v;
      });
      if (hasValue) mapped.push(out);
    });
    return mapped;
  };

  const handleFile = async (file) => {
    if (file.size > MAX_FILE_BYTES) {
      message.error("File is too large (max 2 MB).");
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      message.error("Please upload a .csv or .xlsx file.");
      return;
    }

    try {
      let rawRows;
      if (ext === "csv") {
        rawRows = await new Promise((resolve, reject) =>
          Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (r) => resolve(r.data),
            error: reject,
          })
        );
      } else {
        const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
        const sheet = wb.Sheets.Leads || wb.Sheets[wb.SheetNames[0]];
        rawRows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
      }

      const mapped = mapRows(rawRows);
      if (mapped.length === 0) {
        message.error("No lead rows found. Check the column headers against the template.");
        return;
      }
      if (mapped.length > MAX_ROWS) {
        message.error(`This file has ${mapped.length} rows — the limit is ${MAX_ROWS} per import.`);
        return;
      }
      if (!mapped.some((r) => r.name) || !mapped.some((r) => r.phone)) {
        message.error("Could not find the Name and Phone columns. Please use the template headers.");
        return;
      }
      setFileName(file.name);
      setRows(mapped);
      setResults(null);
      setMissingReport(null);
      await runValidate(mapped, autoCreateMissing);
    } catch (err) {
      console.error(err);
      message.error("Could not read this file.");
    }
  };

  const missingCount = useMemo(() => rows.filter((r) => !r.name || !r.phone).length, [rows]);

  const runImport = async () => {
    // Re-validate as the real gate (covers toggling auto-create after the
    // first check, or waiting a while before clicking Import) -- nothing is
    // sent to the real import endpoint until this passes.
    const ok = await runValidate(rows, autoCreateMissing);
    if (!ok) return;

    setImporting(true);
    setProgress(0);
    const importRunId = newRunId();
    const all = [];
    let done = 0;
    for (const chunk of buildChunks(rows)) {
      try {
        const res = await importLeadsApi(chunk, {
          assigned_to: assigneeId,
          import_run_id: importRunId,
          auto_create_missing: autoCreateMissing,
          file_name: fileName,
          total_rows: rows.length,
        });
        all.push(...(res.data?.results || []));
      } catch (err) {
        chunk.forEach((r) =>
          all.push({ row: r.row, status: "error", message: err.message || "Request failed" })
        );
      }
      done += chunk.length;
      setProgress(Math.round((done / rows.length) * 100));
    }
    setResults(all);
    setImporting(false);
  };

  const summary = useMemo(() => {
    if (!results) return null;
    return {
      imported: results.filter((r) => r.status === "imported").length,
      duplicate: results.filter((r) => r.status === "duplicate").length,
      error: results.filter((r) => r.status === "error").length,
    };
  }, [results]);

  const failedRows = useMemo(
    () =>
      (results || [])
        .filter((r) => r.status !== "imported")
        .map((r) => {
          const src = rows.find((x) => x.row === r.row) || {};
          return { ...r, name: src.name, phone: src.phone };
        }),
    [results, rows]
  );

  const downloadErrorReport = () => {
    const csv = Papa.unparse(
      failedRows.map((r) => ({
        Row: r.row,
        Name: r.name || "",
        Phone: r.phone || "",
        Result: r.status === "duplicate" ? "Skipped (duplicate)" : "Not imported",
        Reason: r.message || "",
      }))
    );
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), "lead_import_errors.csv");
  };

  const previewColumns = [
    { title: "Row", dataIndex: "row", width: 60 },
    { title: "Name", dataIndex: "name", render: (v) => v || <Tag color="red">missing</Tag> },
    { title: "Phone", dataIndex: "phone", render: (v) => v || <Tag color="red">missing</Tag> },
    { title: "Status", dataIndex: "status", render: (v) => v || "-" },
    { title: "Source", dataIndex: "source", render: (v) => v || "-" },
  ];

  const failedColumns = [
    { title: "Row", dataIndex: "row", width: 60 },
    { title: "Name", dataIndex: "name", ellipsis: true },
    {
      title: "Result",
      dataIndex: "status",
      width: 110,
      render: (v) => (v === "duplicate" ? <Tag color="orange">Duplicate</Tag> : <Tag color="red">Error</Tag>),
    },
    { title: "Reason", dataIndex: "message" },
  ];

  return (
    <Modal
      title="Import Leads"
      open={open}
      onCancel={close}
      maskClosable={!importing}
      closable={!importing}
      width={760}
      footer={
        results ? (
          <Button type="primary" onClick={close}>Done</Button>
        ) : (
          <Space>
            <Button onClick={close} disabled={importing}>Cancel</Button>
            <Button
              type="primary"
              disabled={!rows.length || validating || importing || Boolean(missingReport)}
              loading={importing}
              onClick={runImport}
            >
              {rows.length ? `Import ${rows.length} lead${rows.length !== 1 ? "s" : ""}` : "Import"}
            </Button>
          </Space>
        )
      }
    >
      {!results && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Upload a .csv or .xlsx file (max 1000 rows, 2 MB)"
            description={
              <>
                Name and Phone are required. Existing phone numbers are skipped, not overwritten.
              </>
            }
          />
          <Space direction="vertical" style={{ width: "100%", marginBottom: 12 }} size={10}>
            <div>
              <Checkbox
                checked={autoCreateMissing}
                disabled={importing || validating}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  setAutoCreateMissing(checked);
                  if (rows.length) await runValidate(rows, checked);
                }}
              >
                Automatically create missing statuses, sources, purposes, and custom field options
              </Checkbox>
              <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                Each row's own Status/Source/Purpose columns are used directly — one file can import leads
                into many different statuses at once. Leave a cell blank to use this branch's default.
              </Text>
            </div>
            <div>
              <Text strong>Assign leads to</Text>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                value={assigneeId}
                onChange={setAssigneeId}
                disabled={!isAdmin || importing}
                showSearch
                optionFilterProp="label"
                options={
                  isAdmin
                    ? [
                        ...((users || []).some((u) => u.id === currentUserId)
                          ? []
                          : [{ label: currentUserName || "Me", value: currentUserId }]),
                        ...(users || []).map((u) => ({ label: u.full_name, value: u.id })),
                      ]
                    : [{ label: currentUserName || "Me", value: currentUserId }]
                }
              />
            </div>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              Download template
            </Button>
          </Space>
          <Upload.Dragger
            accept=".csv,.xlsx,.xls"
            showUploadList={false}
            maxCount={1}
            disabled={importing}
            beforeUpload={(file) => { handleFile(file); return false; }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">
              {fileName ? fileName : "Click or drag a file here"}
            </p>
          </Upload.Dragger>

          {validating && <Text type="secondary" style={{ display: "block", marginTop: 8 }}>Checking the file against your statuses, sources, purposes and custom fields…</Text>}

          {missingReport && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 12 }}
              message="Some values in this file don't exist yet"
              description={
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {missingReport.status?.length > 0 && (
                    <Text>Non-existent statuses: {missingReport.status.join(", ")}</Text>
                  )}
                  {missingReport.source?.length > 0 && (
                    <Text>Non-existent sources: {missingReport.source.join(", ")}</Text>
                  )}
                  {missingReport.purpose?.length > 0 && (
                    <Text>Non-existent purposes: {missingReport.purpose.join(", ")}</Text>
                  )}
                  {(missingReport.custom_fields || []).map((f) => (
                    <Text key={f.field_key}>"{f.field_name}" has values not in its option list: {f.values.join(", ")}</Text>
                  ))}
                  <Text type="secondary">
                    Tick "Automatically create missing values" above and this will import on its own, or fix these values in the file and re-upload.
                  </Text>
                </Space>
              }
            />
          )}

          {rows.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text strong>{rows.length} row{rows.length !== 1 ? "s" : ""} ready</Text>
              {missingCount > 0 && (
                <Text type="danger"> — {missingCount} missing a name or phone (will be reported, not imported)</Text>
              )}
              <Table
                size="small"
                style={{ marginTop: 8 }}
                rowKey="row"
                columns={previewColumns}
                dataSource={rows.slice(0, 10)}
                pagination={false}
                scroll={{ x: true }}
              />
              {rows.length > 10 && <Text type="secondary">Showing the first 10 rows.</Text>}
            </div>
          )}

          {importing && <Progress percent={progress} style={{ marginTop: 16 }} />}
        </>
      )}

      {results && summary && (
        <>
          <Alert
            type={summary.error || summary.duplicate ? "warning" : "success"}
            showIcon
            style={{ marginBottom: 12 }}
            message={`${summary.imported} imported · ${summary.duplicate} duplicate${summary.duplicate !== 1 ? "s" : ""} skipped · ${summary.error} error${summary.error !== 1 ? "s" : ""}`}
          />
          {failedRows.length > 0 && (
            <>
              <Button icon={<DownloadOutlined />} onClick={downloadErrorReport} style={{ marginBottom: 8 }}>
                Download error report
              </Button>
              <Table
                size="small"
                rowKey="row"
                columns={failedColumns}
                dataSource={failedRows}
                pagination={{ pageSize: 8, showSizeChanger: false }}
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default LeadImportModal;
